import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { ZodFormattedError } from "zod";
import type { AppRouter } from "@app-trpc/server/api/trpc/[trpc]";
import { useQueryClient, type MutationOptions } from "@tanstack/vue-query";

export type FindGamesOnRobloxInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["findGamesOnRoblox"];
export type FindGamesInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["findGames"];
export type FindGamesOutput =
  inferRouterOutputs<AppRouter>["games"]["repository"]["findGames"];
export type FindGameInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["findGame"];
export type FindGameOutput =
  inferRouterOutputs<AppRouter>["games"]["repository"]["findGame"];
export type AddGameOrUpdateGameSettingsInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["addGameOrUpdateGameSettings"];
export type AddGameOrUpdateGameSettingsOutput =
  inferRouterOutputs<AppRouter>["games"]["repository"]["addGameOrUpdateGameSettings"];
export type GetGameSortsByGameInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["getGameSortsByGame"];
export type FindGamePassesInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["findGamePasses"];
export type FindGameBadgesInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["findGameBadges"];
export type FetchGamesBadgesFromRobloxInput =
  inferRouterInputs<AppRouter>["games"]["repository"]["fetchGamesBadgesFromRoblox"];

export const useGamesRepositoryClient = () => {
  const client = useNuxtApp().$client;
  const queryClient = useQueryClient();

  function getWebsiteGamesDataQueryOptions() {
    return {
      queryKey: ["games", "repository", "getWebsiteGamesData"],
      queryFn: () => client.games.repository.getWebsiteGamesData.query(),
      refetchInterval: 1000 * 60, // 1 minute
    };
  }

  function getFindGamesOnRobloxQueryOptions(input: FindGamesOnRobloxInput) {
    return {
      queryKey: ["games", "repository", "findGamesOnRoblox", input],
      queryFn: () => client.games.repository.findGamesOnRoblox.query(input),
      staleTime: 1000 * 60 * 10, // 10 minutes
    };
  }

  function getFindGamesQueryOptions(input: FindGamesInput) {
    return {
      queryKey: ["games", "repository", "findGames", input],
      queryFn: async () => {
        const result = await client.games.repository.findGames.query(input);
        result.data.forEach((game) => {
          queryClient.setQueryData(
            getFindGameQueryOptions({
              where: {
                universeId: game.universeId,
              },
            }).queryKey,
            {
              data: game,
            }
          );
        });
        return result;
      },
      staleTime: 1000 * 60, // 1 minute
    };
  }

  function getFindGameQueryOptions(input: FindGameInput) {
    return {
      queryKey: ["games", "repository", "findGame", input],
      queryFn: () => client.games.repository.findGame.query(input),
      staleTime: 1000 * 60, // 1 minute
    };
  }
  function addGameOrUpdateGameSettingsMutationOptions(): MutationOptions<
    AddGameOrUpdateGameSettingsOutput,
    ZodFormattedError<AddGameOrUpdateGameSettingsInput>,
    AddGameOrUpdateGameSettingsInput
  > {
    return {
      mutationFn: async (data: AddGameOrUpdateGameSettingsInput) => {
        try {
          return client.games.repository.addGameOrUpdateGameSettings.mutate(
            data
          );
        } catch (e) {
          if (isTRPCClientError(e)) {
            const zodError = e.data
              ?.zodError as ZodFormattedError<AddGameOrUpdateGameSettingsInput>;
            throw zodError;
          }
          throw e;
        }
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "games",
            "repository",
            "findGame",
            {
              where: {
                universeId: variables.where.universeId,
              },
            },
          ],
        });
      },
    };
  }

  function getGameSortsByGameQueryOptions(input: GetGameSortsByGameInput) {
    return {
      queryKey: ["games", "repository", "getGameSortsByGame", input],
      queryFn: () => client.games.repository.getGameSortsByGame.query(input),
    };
  }

  function getFindGamePassesQueryOptions(input: FindGamePassesInput) {
    return {
      queryKey: ["games", "repository", "findGamePasses", input],
      queryFn: () => client.games.repository.findGamePasses.query(input),
    };
  }

  function getFindGameBadgesQueryOptions(input: FindGameBadgesInput) {
    return {
      queryKey: ["games", "repository", "findGameBadges", input],
      queryFn: () => client.games.repository.findGameBadges.query(input),
    };
  }

  function getFetchGamesBadgesFromRobloxQueryOptions(
    input: FetchGamesBadgesFromRobloxInput
  ) {
    return {
      queryKey: ["games", "repository", "fetchGamesBadgesFromRoblox", input],
      queryFn: () =>
        client.games.repository.fetchGamesBadgesFromRoblox.query(input),
    };
  }

  return {
    getWebsiteGamesDataQueryOptions,
    getFindGamesOnRobloxQueryOptions,
    getFindGamesQueryOptions,
    getFindGameQueryOptions,
    addGameOrUpdateGameSettingsMutationOptions,
    getGameSortsByGameQueryOptions,
    getFindGamePassesQueryOptions,
    getFindGameBadgesQueryOptions,
    getFetchGamesBadgesFromRobloxQueryOptions,
  };
};
